'use client'
import { useAuthUser } from 'lib/auth'
import Link from 'next/link'

function GridPattern() {
  return (
    <div className='absolute inset-0 overflow-hidden pointer-events-none'>
      <div className='absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]' />
      <div className='absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black' />
    </div>
  )
}

function GlowOrb({ className }: { className?: string }) {
  return (
    <div
      className={`absolute rounded-full blur-[100px] opacity-20 ${className}`}
    />
  )
}

function Navbar({ user }: { user: ReturnType<typeof useAuthUser> }) {
  return (
    <nav className='fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-black/50 backdrop-blur-xl'>
      <div className='max-w-6xl mx-auto px-6 h-16 flex items-center justify-between'>
        <div className='flex items-center gap-8'>
          <Link href='/' className='text-white font-semibold text-lg tracking-tight'>
            Lunara
          </Link>
          <div className='hidden md:flex items-center gap-6'>
            <a href='#features' className='text-sm text-zinc-400 hover:text-white transition-colors'>
              Features
            </a>
            <a href='#strategies' className='text-sm text-zinc-400 hover:text-white transition-colors'>
              Strategies
            </a>
            <a href='#exchanges' className='text-sm text-zinc-400 hover:text-white transition-colors'>
              Exchanges
            </a>
            <Link href='/legal/terms' className='text-sm text-zinc-400 hover:text-white transition-colors'>
              Terms
            </Link>
          </div>
        </div>
        <div className='flex items-center gap-4'>
          {user ? (
            <Link
              href='/console/dashboard'
              className='text-sm px-4 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors'
            >
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                href='/login'
                className='text-sm text-zinc-400 hover:text-white transition-colors'
              >
                Sign In
              </Link>
              <Link
                href='/login'
                className='text-sm px-4 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors'
              >
                Get Started
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}

function Hero() {
  return (
    <section className='relative min-h-screen flex items-center justify-center pt-16'>
      <GridPattern />
      <GlowOrb className='w-[600px] h-[600px] bg-blue-500 -top-40 -left-40' />
      <GlowOrb className='w-[400px] h-[400px] bg-purple-500 top-1/2 -right-20' />
      
      <div className='relative z-10 max-w-4xl mx-auto px-6 text-center'>
        <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 mb-8'>
          <span className='relative flex h-2 w-2'>
            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75' />
            <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-500' />
          </span>
          <span className='text-sm text-zinc-400'>80+ algorithmic strategies running 24/7</span>
        </div>
        
        <h1 className='text-5xl md:text-7xl font-bold tracking-tight text-white mb-6'>
          Automated crypto
          <br />
          <span className='bg-gradient-to-r from-zinc-200 via-zinc-400 to-zinc-600 bg-clip-text text-transparent'>
            trading infrastructure
          </span>
        </h1>
        
        <p className='text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10'>
          Deploy algorithmic trading strategies across Binance, Bybit, Hyperliquid, and more.
          Backtest with historical data. Manage portfolios with intelligent asset rotation.
        </p>
        
        <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
          <Link
            href='/login'
            className='w-full sm:w-auto px-8 py-3 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-all hover:scale-105'
          >
            Start Trading
          </Link>
          <a
            href='#features'
            className='w-full sm:w-auto px-8 py-3 rounded-lg border border-white/10 text-white font-medium hover:bg-white/5 transition-all'
          >
            View Features
          </a>
        </div>
        
        <div className='mt-20 grid grid-cols-3 gap-8 max-w-lg mx-auto'>
          <div>
            <div className='text-3xl font-bold text-white font-mono'>80+</div>
            <div className='text-sm text-zinc-500 mt-1'>Strategies</div>
          </div>
          <div>
            <div className='text-3xl font-bold text-white font-mono'>5</div>
            <div className='text-sm text-zinc-500 mt-1'>Exchanges</div>
          </div>
          <div>
            <div className='text-3xl font-bold text-white font-mono'>24/7</div>
            <div className='text-sm text-zinc-500 mt-1'>Automated</div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Features() {
  const features = [
    {
      title: 'Multi-Strategy Trading',
      description: 'Choose from 80+ strategies including Trend Following, Mean Reversion, Bollinger Bands, RSI, MACD, and Z-Score based approaches.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' />
        </svg>
      )
    },
    {
      title: 'Portfolio Management',
      description: 'Create portfolios with Z-score based asset selection, automatic rebalancing, and configurable allocation strategies across multiple assets.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z' />
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z' />
        </svg>
      )
    },
    {
      title: 'Advanced Backtesting',
      description: 'Test strategies against historical OHLCV data. Analyze Sharpe ratio, CAGR, maximum drawdown, and per-strategy performance breakdowns.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' />
        </svg>
      )
    },
    {
      title: 'Risk Management',
      description: 'Automated stop-loss and take-profit orders. Position deleveraging based on P&L thresholds. Configurable max loss limits per strategy.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' />
        </svg>
      )
    },
    {
      title: 'Multi-Region Deployment',
      description: 'Bots deploy to AWS regions closest to each exchange. Run simultaneously across us-east-1, eu-west-1, and ap-northeast-1.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
      )
    },
    {
      title: 'Real-Time Alerts',
      description: 'Instant Slack notifications for position opens, closes, order executions, and P&L updates. Bot health monitoring with heartbeat tracking.',
      icon: (
        <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' />
        </svg>
      )
    }
  ]

  return (
    <section id='features' className='relative py-32 border-t border-white/5'>
      <div className='max-w-6xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
            Professional trading infrastructure
          </h2>
          <p className='text-zinc-400 max-w-xl mx-auto'>
            Everything you need to run algorithmic trading strategies at scale.
          </p>
        </div>
        
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {features.map((feature) => (
            <div
              key={feature.title}
              className='group p-6 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 transition-all'
            >
              <div className='w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 group-hover:text-white group-hover:bg-white/10 transition-all mb-4'>
                {feature.icon}
              </div>
              <h3 className='text-lg font-semibold text-white mb-2'>{feature.title}</h3>
              <p className='text-sm text-zinc-400 leading-relaxed'>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Strategies() {
  const strategyCategories = [
    {
      name: 'Trend Following',
      strategies: ['Momentum', 'Moving Average Crossover', 'Breakout', 'ADX Trend'],
      description: 'Capture sustained price movements'
    },
    {
      name: 'Mean Reversion',
      strategies: ['Z-Score', 'RSI Oversold/Overbought', 'Bollinger Band Bounce'],
      description: 'Trade price corrections to the mean'
    },
    {
      name: 'Volatility Based',
      strategies: ['Bollinger Band Squeeze', 'ATR Breakout', 'Range Expansion'],
      description: 'Capitalize on volatility changes'
    },
    {
      name: 'Volume Analysis',
      strategies: ['Volume Profile', 'KVO (Klinger)', 'Volume Breakout'],
      description: 'Trade based on volume patterns'
    }
  ]

  return (
    <section id='strategies' className='relative py-32 border-t border-white/5'>
      <GlowOrb className='w-[500px] h-[500px] bg-blue-500 top-0 left-1/4' />
      
      <div className='relative z-10 max-w-6xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
            80+ built-in strategies
          </h2>
          <p className='text-zinc-400 max-w-xl mx-auto'>
            From trend following to mean reversion, choose strategies that match your trading style.
          </p>
        </div>
        
        <div className='grid md:grid-cols-2 gap-6'>
          {strategyCategories.map((category) => (
            <div
              key={category.name}
              className='p-6 rounded-xl border border-white/5 bg-white/[0.02]'
            >
              <h3 className='text-lg font-semibold text-white mb-1'>{category.name}</h3>
              <p className='text-sm text-zinc-500 mb-4'>{category.description}</p>
              <div className='flex flex-wrap gap-2'>
                {category.strategies.map((strategy) => (
                  <span
                    key={strategy}
                    className='px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-zinc-300 border border-white/10'
                  >
                    {strategy}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className='mt-12 text-center'>
          <p className='text-zinc-500 text-sm'>
            Plus: Ichimoku Cloud, MACD, Stochastic, CCI, Williams %R, Donchian Channel, and many more
          </p>
        </div>
      </div>
    </section>
  )
}

function Exchanges() {
  const exchanges = [
    { name: 'Binance', types: ['Spot', 'USDM Futures', 'COINM Futures'] },
    { name: 'Bybit', types: ['Perpetuals', 'Spot'] },
    { name: 'Hyperliquid', types: ['Perpetuals'] },
    { name: 'Deribit', types: ['Options', 'Futures'] },
    { name: 'Alpaca', types: ['Crypto', 'Stocks'] }
  ]

  return (
    <section id='exchanges' className='relative py-32 border-t border-white/5'>
      <div className='max-w-6xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
            Connected to major exchanges
          </h2>
          <p className='text-zinc-400 max-w-xl mx-auto'>
            Trade across multiple exchanges with unified portfolio management.
          </p>
        </div>
        
        <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
          {exchanges.map((exchange) => (
            <div
              key={exchange.name}
              className='p-6 rounded-xl border border-white/5 bg-white/[0.02] text-center'
            >
              <div className='text-lg font-semibold text-white mb-2'>{exchange.name}</div>
              <div className='text-xs text-zinc-500'>
                {exchange.types.join(' / ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Dashboard() {
  return (
    <section className='relative py-32 border-t border-white/5'>
      <GlowOrb className='w-[500px] h-[500px] bg-emerald-500 top-0 left-1/2 -translate-x-1/2' />
      
      <div className='relative z-10 max-w-6xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
            Complete trading dashboard
          </h2>
          <p className='text-zinc-400 max-w-xl mx-auto'>
            Monitor positions, analyze P&L, and manage your portfolios from a single interface.
          </p>
        </div>

        <div className='rounded-xl border border-white/10 bg-zinc-950 overflow-hidden'>
          <div className='flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-zinc-900/50'>
            <div className='w-3 h-3 rounded-full bg-red-500/80' />
            <div className='w-3 h-3 rounded-full bg-yellow-500/80' />
            <div className='w-3 h-3 rounded-full bg-green-500/80' />
            <span className='ml-2 text-xs text-zinc-500 font-mono'>console.lunara.app</span>
          </div>
          <div className='p-6'>
            <div className='grid grid-cols-4 gap-4 mb-6'>
              <div className='p-4 rounded-lg bg-white/5 border border-white/5'>
                <div className='text-xs text-zinc-500 mb-1'>Open Positions</div>
                <div className='text-2xl font-bold text-white font-mono'>12</div>
              </div>
              <div className='p-4 rounded-lg bg-white/5 border border-white/5'>
                <div className='text-xs text-zinc-500 mb-1'>Unrealized P&L</div>
                <div className='text-2xl font-bold text-emerald-400 font-mono'>+$2,847</div>
              </div>
              <div className='p-4 rounded-lg bg-white/5 border border-white/5'>
                <div className='text-xs text-zinc-500 mb-1'>Active Bots</div>
                <div className='text-2xl font-bold text-white font-mono'>8</div>
              </div>
              <div className='p-4 rounded-lg bg-white/5 border border-white/5'>
                <div className='text-xs text-zinc-500 mb-1'>Win Rate</div>
                <div className='text-2xl font-bold text-white font-mono'>67%</div>
              </div>
            </div>
            
            <div className='rounded-lg bg-white/5 border border-white/5 overflow-hidden'>
              <div className='px-4 py-2 border-b border-white/5 text-xs text-zinc-500 font-medium'>
                Recent Positions
              </div>
              <div className='divide-y divide-white/5'>
                {[
                  { symbol: 'BTC/USDT', side: 'Long', pnl: '+$1,240', pnlPercent: '+4.2%', strategy: 'Trend Following' },
                  { symbol: 'ETH/USDT', side: 'Short', pnl: '+$820', pnlPercent: '+2.8%', strategy: 'Mean Reversion' },
                  { symbol: 'SOL/USDT', side: 'Long', pnl: '+$456', pnlPercent: '+5.1%', strategy: 'Momentum' },
                ].map((position) => (
                  <div key={position.symbol} className='px-4 py-3 flex items-center justify-between text-sm'>
                    <div className='flex items-center gap-4'>
                      <span className='text-white font-medium'>{position.symbol}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${position.side === 'Long' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {position.side}
                      </span>
                      <span className='text-zinc-500'>{position.strategy}</span>
                    </div>
                    <div className='text-right'>
                      <div className='text-emerald-400 font-mono'>{position.pnl}</div>
                      <div className='text-xs text-zinc-500'>{position.pnlPercent}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Workflow() {
  const steps = [
    {
      step: '01',
      title: 'Connect Exchange',
      description: 'Add your exchange API credentials. We support Binance, Bybit, Hyperliquid, Deribit, and Alpaca.'
    },
    {
      step: '02',
      title: 'Create Portfolio',
      description: 'Configure trading direction, select strategies, and set up asset allocation rules with Z-score based selection.'
    },
    {
      step: '03',
      title: 'Backtest Strategies',
      description: 'Test against historical data. Review Sharpe ratio, drawdown, and performance metrics before going live.'
    },
    {
      step: '04',
      title: 'Deploy & Monitor',
      description: 'Bots run 24/7 across AWS regions. Track positions, P&L, and receive Slack alerts in real-time.'
    }
  ]

  return (
    <section className='relative py-32 border-t border-white/5'>
      <div className='max-w-6xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
            From strategy to execution
          </h2>
          <p className='text-zinc-400 max-w-xl mx-auto'>
            Get your trading bots running in minutes, not days.
          </p>
        </div>
        
        <div className='grid md:grid-cols-4 gap-6'>
          {steps.map((item, index) => (
            <div key={item.step} className='relative'>
              {index < steps.length - 1 && (
                <div className='hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-white/10 to-transparent' />
              )}
              <div className='text-4xl font-bold text-white/10 mb-4'>{item.step}</div>
              <h3 className='text-lg font-semibold text-white mb-2'>{item.title}</h3>
              <p className='text-sm text-zinc-400 leading-relaxed'>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTA() {
  return (
    <section className='relative py-32 border-t border-white/5'>
      <div className='max-w-2xl mx-auto px-6 text-center'>
        <h2 className='text-3xl md:text-4xl font-bold text-white mb-4'>
          Ready to automate your trading?
        </h2>
        <p className='text-zinc-400 mb-8'>
          Deploy algorithmic strategies across multiple exchanges with professional-grade infrastructure.
        </p>
        <Link
          href='/login'
          className='inline-flex px-8 py-3 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-all hover:scale-105'
        >
          Get Started
        </Link>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className='border-t border-white/5 py-12'>
      <div className='max-w-6xl mx-auto px-6'>
        <div className='flex flex-col md:flex-row items-center justify-between gap-4'>
          <div className='text-sm text-zinc-500'>
            &copy; {new Date().getFullYear()} Lunara Capital, LLC. All rights reserved.
          </div>
          <div className='flex items-center gap-6'>
            <Link href='/legal/terms' className='text-sm text-zinc-500 hover:text-white transition-colors'>
              Terms
            </Link>
            <Link href='/legal/privacy' className='text-sm text-zinc-500 hover:text-white transition-colors'>
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default function LandingPage() {
  const user = useAuthUser()

  return (
    <div className='min-h-screen bg-black'>
      <Navbar user={user} />
      <Hero />
      <Features />
      <Strategies />
      <Exchanges />
      <Dashboard />
      <Workflow />
      <CTA />
      <Footer />
    </div>
  )
}
